"use client"

import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import Image from "next/image"
import Link from "next/link"
import { cn } from "@/lib/utils"
import { buttonVariants } from "@/components/ui/button"
import { useTranslations } from "@/lib/intl/use-translations"
import { useLocalePath } from "@/lib/use-localized-navigation"
import { ReviewsType } from "@/components/ui/reviews-rail"
import { cmsMediaUrl, type Testimonial } from "@/lib/cms"
import {
  StarIcon,
  GoogleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@/components/ui/icons"

const GOOGLE_REVIEWS_URL =
  "https://www.google.com/maps/place/Ahead+Health/@47.3734617,8.5311033,17z/data=!4m18!1m9!3m8!1s0x47900b29f69aa4c5:0xec34f49e3ca9ce2e!2sAhead+Health!8m2!3d47.3734618!4d8.5359742!9m1!1b1!16s%2Fg%2F11x1bl_tpp!3m7!1s0x47900b29f69aa4c5:0xec34f49e3ca9ce2e!8m2!3d47.3734618!4d8.5359742!9m1!1b1!16s%2Fg%2F11x1bl_tpp"

/** Cards visible at once — one on mobile, three from `md` up. */
const PER_VIEW_DESKTOP = 3

/**
 * Testimonials as a row of white cards (three up on desktop, one per view on
 * mobile), paged by a scroll-snap slider with dots + arrows once there are
 * more reviews than fit on screen.
 *
 * Experiment (2026-07-29): rendered directly below `CyclingTestimonialBar` on
 * the test detail pages so the dark single-quote bar and this card row can be
 * compared side by side. One of the two comes out before this ships.
 *
 * Paging is driven by native scroll + `scroll-snap` rather than mounting and
 * unmounting slides, so there's no `AnimatePresence` here and nothing that
 * could bail the page to CSR (see `/animation-cache-trap`).
 */
export function TestimonialCardsRow({
  customerCountLabel,
  cmsTestimonials,
  tone = "default",
}: {
  customerCountLabel: string
  cmsTestimonials?: Testimonial[]
  /** "plan": the membership-first site's blood test page, white cards on
   *  the grey page, bigger stars and a tighter rhythm (Eugene, 01.10). */
  tone?: "default" | "plan"
}) {
  const plan = tone === "plan"
  const ut = useTranslations("ui")
  const lp = useLocalePath()
  // Same normalization as CyclingTestimonialBar: CMS text reviews only, with
  // the review text living in `headline` + optional longer `quote`.
  const reviews = useMemo(
    () =>
      (cmsTestimonials ?? [])
        .filter((c) => c.type === "text")
        .map((c) => ({
          id: c.id,
          quote: [c.headline, c.quote].filter(Boolean).join(" ").trim(),
          headline: c.headline?.trim(),
          body: c.quote?.trim(),
          author: c.authorName,
          avatarSrc: c.avatar?.url ? cmsMediaUrl(c.avatar.url) : undefined,
          source: c.source,
          rating: c.rating ?? 5,
        }))
        .filter((r) => r.quote),
    [cmsTestimonials]
  )

  const scrollRef = useRef<HTMLDivElement>(null)
  const cardRefs = useRef<(HTMLElement | null)[]>([])
  const [perView, setPerView] = useState(1)
  const [page, setPage] = useState(0)

  const pageCount = Math.max(1, Math.ceil(reviews.length / perView))

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)")
    const sync = () => setPerView(mq.matches ? PER_VIEW_DESKTOP : 1)
    sync()
    mq.addEventListener("change", sync)
    return () => mq.removeEventListener("change", sync)
  }, [])

  // Derive the active page from scroll position: whichever card sits closest
  // to the container's left edge is the first card of the current page.
  const syncPage = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    let nearest = 0
    let best = Infinity
    cardRefs.current.forEach((card, i) => {
      if (!card) return
      const delta = Math.abs(card.offsetLeft - el.scrollLeft)
      if (delta < best) {
        best = delta
        nearest = i
      }
    })
    setPage(Math.floor(nearest / perView))
  }, [perView])

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    let raf = 0
    const onScroll = () => {
      if (raf !== 0) return
      raf = requestAnimationFrame(() => {
        raf = 0
        syncPage()
      })
    }
    el.addEventListener("scroll", onScroll, { passive: true })
    syncPage()
    return () => {
      el.removeEventListener("scroll", onScroll)
      if (raf !== 0) cancelAnimationFrame(raf)
    }
  }, [syncPage])

  const goToPage = (p: number) => {
    const el = scrollRef.current
    const card = cardRefs.current[p * perView]
    if (!el || !card) return
    el.scrollTo({ left: card.offsetLeft, behavior: "smooth" })
  }

  if (reviews.length === 0) return null

  const showControls = reviews.length > perView

  return (
    <section className={cn(plan ? "py-12 md:py-16" : "py-16 md:py-20")}>
      {plan && <ReviewsType />}
      <div
        className={cn(
          "mx-auto px-4 md:px-10",
          plan ? "max-w-[1200px]" : "max-w-[1440px]"
        )}
      >
        {/* Social-proof header */}
        <div
          className={cn("flex flex-col items-center", plan ? "gap-1" : "gap-2")}
        >
          <a
            href={GOOGLE_REVIEWS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5"
          >
            <GoogleIcon width={plan ? 22 : 16} height={plan ? 22 : 16} />
            {plan ? (
              // Plan: the stars sit tighter together (Eugene, 01.10).
              <span className="flex gap-0.5">
                {Array.from({ length: 5 }, (_, i) => (
                  <StarIcon key={i} width={22} height={22} />
                ))}
              </span>
            ) : (
              Array.from({ length: 5 }, (_, i) => (
                <StarIcon key={i} width={16} height={16} />
              ))
            )}
          </a>
          <p
            className={cn(
              "text-neutral-600",
              plan ? "type-body-md" : "type-body-lg"
            )}
          >
            {customerCountLabel}
          </p>
        </div>

        {/* Cards — a scroll-snap slider that happens to show three at a time */}
        <div
          ref={scrollRef}
          className={cn(
            "hide-scrollbar flex snap-x snap-mandatory gap-5 overflow-x-auto scroll-smooth",
            plan ? "mt-8" : "mt-10"
          )}
        >
          {reviews.map((review, i) => (
            <article
              key={review.id}
              ref={(node) => {
                cardRefs.current[i] = node
              }}
              className={cn(
                "flex min-w-full snap-start flex-col rounded-[16px] bg-white md:min-w-[calc((100%-2.5rem)/3)]",
                plan ? "p-7 md:p-8" : "justify-center p-8 text-center"
              )}
            >
              {plan ? (
                <>
                  {/* The homepage rail's text card type: headline 20/24.75
                      Medium, body and name body-xl (Eugene, 01.10). */}
                  <div className="flex flex-col gap-4">
                    <p
                      className={cn(
                        "hv3-rev-headline text-neutral-900",
                        review.headline && review.body
                          ? "line-clamp-2"
                          : "line-clamp-5"
                      )}
                    >
                      &ldquo;{review.headline || review.quote}&rdquo;
                    </p>
                    {review.headline && review.body ? (
                      <p className="type-body-xl line-clamp-3 text-neutral-950">
                        {review.body}
                      </p>
                    ) : null}
                  </div>
                  <figure className="mt-7 flex items-center gap-3">
                    {review.avatarSrc ? (
                      <div className="relative size-10 shrink-0 overflow-hidden rounded-full bg-neutral-100">
                        <Image
                          src={review.avatarSrc}
                          alt=""
                          fill
                          sizes="40px"
                          className="object-cover"
                        />
                      </div>
                    ) : (
                      <span
                        className="size-10 shrink-0 rounded-full bg-neutral-200"
                        aria-hidden
                      />
                    )}
                    <figcaption>
                      <p className="type-body-xl text-neutral-900">
                        {review.author}
                      </p>
                      <div
                        className="mt-1 flex"
                        aria-label={`${review.rating} out of 5 stars`}
                      >
                        {Array.from({ length: review.rating }, (_, s) => (
                          <StarIcon key={s} width={14} height={14} />
                        ))}
                      </div>
                    </figcaption>
                  </figure>
                </>
              ) : (
                <>
                  <div className="flex justify-center gap-1">
                    {Array.from({ length: review.rating }, (_, s) => (
                      <StarIcon key={s} width={14} height={14} />
                    ))}
                  </div>
                  <p className="type-body-lg mt-5 line-clamp-6 text-neutral-700">
                    &ldquo;{review.quote}&rdquo;
                  </p>
                  <div className="mt-6 flex items-center justify-center gap-3">
                    {review.avatarSrc && (
                      <div className="relative h-8 w-8 flex-shrink-0 overflow-hidden rounded-full bg-neutral-100">
                        <Image
                          src={review.avatarSrc}
                          alt=""
                          fill
                          sizes="32px"
                          className="object-cover"
                        />
                      </div>
                    )}
                    <p className="type-body-md text-neutral-500">
                      {review.author} ·{" "}
                      {review.source === "google" ? "Google" : "Trustpilot"}
                    </p>
                  </div>
                </>
              )}
            </article>
          ))}
        </div>

        {/* Plan: the homepage rail's foot, an "All reviews" pill centred and
            two arrows at the right edge (Eugene, 01.10). */}
        {plan && (
          <div className="mt-6 flex items-center justify-between md:mt-[22px]">
            <span className="hidden w-[88px] md:block" aria-hidden />
            <Link
              href={lp("/reviews")}
              className={cn(
                buttonVariants({ variant: "outline" }),
                "mx-auto px-8 md:mx-0"
              )}
            >
              {ut("viewAllReviews")}
            </Link>
            <div
              className={cn(
                "hidden gap-2",
                showControls ? "md:flex" : "md:invisible md:flex"
              )}
            >
              <button
                type="button"
                onClick={() => goToPage(Math.max(0, page - 1))}
                disabled={page === 0}
                aria-label="Previous reviews"
                className="flex size-10 cursor-pointer items-center justify-center rounded-full bg-white text-neutral-900 transition-colors hover:bg-neutral-100 disabled:cursor-default disabled:text-neutral-300 disabled:hover:bg-white"
              >
                <ChevronLeftIcon width={24} height={24} />
              </button>
              <button
                type="button"
                onClick={() => goToPage(Math.min(pageCount - 1, page + 1))}
                disabled={page === pageCount - 1}
                aria-label="Next reviews"
                className="flex size-10 cursor-pointer items-center justify-center rounded-full bg-white text-neutral-900 transition-colors hover:bg-neutral-100 disabled:cursor-default disabled:text-neutral-300 disabled:hover:bg-white"
              >
                <ChevronRightIcon width={24} height={24} />
              </button>
            </div>
          </div>
        )}

        {/* Dots + arrows — only once there's more than one page of reviews */}
        {!plan && showControls && (
          <div
            className={cn(
              "flex items-center justify-center gap-6",
              plan ? "mt-6" : "mt-8"
            )}
          >
            <button
              type="button"
              onClick={() => goToPage(Math.max(0, page - 1))}
              disabled={page === 0}
              aria-label="Previous reviews"
              className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border border-neutral-300 text-neutral-600 transition-colors hover:border-neutral-900 hover:text-neutral-900 disabled:cursor-default disabled:opacity-30 disabled:hover:border-neutral-300 disabled:hover:text-neutral-600"
            >
              <ChevronLeftIcon width={16} height={16} />
            </button>
            <div className="flex items-center gap-2">
              {Array.from({ length: pageCount }, (_, p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => goToPage(p)}
                  aria-label={`Go to reviews page ${p + 1}`}
                  aria-current={p === page}
                  className={cn(
                    "h-2 cursor-pointer rounded-full transition-all",
                    p === page
                      ? "w-6 bg-neutral-900"
                      : "w-2 bg-neutral-300 hover:bg-neutral-400"
                  )}
                />
              ))}
            </div>
            <button
              type="button"
              onClick={() => goToPage(Math.min(pageCount - 1, page + 1))}
              disabled={page === pageCount - 1}
              aria-label="Next reviews"
              className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border border-neutral-300 text-neutral-600 transition-colors hover:border-neutral-900 hover:text-neutral-900 disabled:cursor-default disabled:opacity-30 disabled:hover:border-neutral-300 disabled:hover:text-neutral-600"
            >
              <ChevronRightIcon width={16} height={16} />
            </button>
          </div>
        )}
      </div>
    </section>
  )
}
